import React, { useState } from 'react';

import { tableBorderSizes } from '@aline/common';
import type {
  InsertTableColumnLocation,
  InsertTableRowLocation,
  TableBorderSize,
  TableElement,
} from '@aline/common';
import { Divider, Typography } from '@mui/material';
import { equals } from 'ramda';
import { AlineTooltip, CheckMarkMenuItem, SelectButton, ToolbarButton } from 'src/components';
import { CenterAlignIcon } from 'src/components/icons/CenterAlignIcon';
import { DeleteTableColumnsIcon } from 'src/components/icons/DeleteTableColumnsIcon';
import { DeleteTableRowsIcon } from 'src/components/icons/DeleteTableRowsIcon';
import { InsertTableColumnsLeftIcon } from 'src/components/icons/InsertTableColumnsLeftIcon';
import { InsertTableColumnsRightIcon } from 'src/components/icons/InsertTableColumnsRightIcon';
import { InsertTableRowsAboveIcon } from 'src/components/icons/InsertTableRowsAboveIcon';
import { InsertTableRowsBelowIcon } from 'src/components/icons/InsertTableRowsBelowIcon';
import { LeftAlignIcon } from 'src/components/icons/LeftAlignIcon';
import { PageSizeIcon } from 'src/components/icons/PageSizeIcon';
import { PropertiesIcon } from 'src/components/icons/PropertiesIcon';
import { RightAlignIcon } from 'src/components/icons/RightAlignIcon';
import { TableBorderWeightIcon } from 'src/components/icons/TableBorderWeightIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { ButtonMenu, tablePropertiesDialogOpenVar } from 'src/editor/toolbars';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading, useSlateSelector } from 'src/layout-engine/hooks';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import { TableBorderColorSelect } from './TableBorderColorSelect';
import { TableCellFillSelect } from './TableCellFillSelect';
import { useMakeTableRectangularAction } from './use-make-table-rectangular-action';
import { useTableCellMergeSplitActions } from './use-table-cell-merge-split-actions';
import type { TableAction } from './use-table-cell-merge-split-actions';
import {
  ButtonsBarWithOverflowMenu,
  useCloseOverflowMenu,
} from '../shared/ButtonsBarWithOverflowMenu';

const useStyles = makeStyles()(theme => ({
  button: {
    marginLeft: theme.spacing(0.5),
  },
  buttonMenuDivider: {
    height: 24,
    marginLeft: theme.spacing(0.8),
    marginRight: theme.spacing(0.8),
  },
}));

export function TableButtons(): JSX.Element {
  const editor = useEditor();
  const { classes } = useStyles();
  const editorLoading = useEditorLoading();
  const { editingMode } = useEditingMode();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { isNestedTable, isSelectionWithinTable } = useSlateSelector(
    Queries.getIsSelectionWithinTable,
    equals
  );
  const [borderWeightOpen, setBorderWeightOpen] = useState<boolean>(false);
  const disableTableButtons = editorLoading || !isSelectionWithinTable;

  const activeBorderWeight = useEditorTabStateSelector(
    state => state.derived.tableProperties.activeBorderWeight
  );
  const align = useEditorTabStateSelector(state => state.derived.tableProperties.align);
  const tableActions = [...useTableCellMergeSplitActions(), useMakeTableRectangularAction()].filter(
    ({ visible }) => visible
  );

  function deleteColumns(): void {
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.deleteTableColumns(editor, {
      authorId: currentUserId,
      editingMode,
    });
    ClientTransforms.focus(editor);
  }

  function deleteRows(): void {
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.deleteTableRows(editor, {
      authorId: currentUserId,
      editingMode,
    });
    ClientTransforms.focus(editor);
  }

  function deleteTable(): void {
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.deleteTable(editor, {
      authorId: currentUserId,
      editingMode,
    });
    ClientTransforms.focus(editor);
  }

  function insertColumns(columnLocation: InsertTableColumnLocation): void {
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.insertTableColumns(editor, {
      authorId: currentUserId,
      columnLocation,
      editingMode,
    });
    ClientTransforms.focus(editor);
  }

  function insertRows(rowLocation: InsertTableRowLocation): void {
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.insertTableRows(editor, {
      authorId: currentUserId,
      editingMode,
      rowLocation,
    });
    ClientTransforms.focus(editor);
  }

  function changeBorderWeight(size: TableBorderSize): void {
    if (!currentUserId || !editingMode) return;

    setBorderWeightOpen(false);

    AuthorizedTransforms.setSelectedTableCellsBorder(editor, {
      authorId: currentUserId,
      borderProps: { size },
      editingMode,
    });
    ClientTransforms.focus(editor);
  }

  function alignTable(align: TableElement['align']) {
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.setTableProps(editor, {
      authorId: currentUserId,
      editingMode,
      props: {
        align,
        indent: { type: 'nil' },
      },
    });
    ClientTransforms.focus(editor);
  }

  function fitTableToContainer(): void {
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.fitTableToContainer(editor, {
      authorId: currentUserId,
      editingMode,
    });
    ClientTransforms.focus(editor);
  }

  return (
    <ButtonsBarWithOverflowMenu>
      <ButtonMenu
        disabled={disableTableButtons}
        icon={<TableBorderWeightIcon />}
        open={borderWeightOpen}
        tooltip="Border width"
        onClose={(): void => setBorderWeightOpen(false)}
        onOpen={(): void => setBorderWeightOpen(true)}
      >
        {tableBorderSizes.map(weight => {
          return (
            <CheckMarkMenuItem
              key={weight}
              showCheckMark={activeBorderWeight === weight}
              onClick={() => changeBorderWeight(weight)}
            >
              <Typography variant="body1">{weight} pt</Typography>
            </CheckMarkMenuItem>
          );
        })}
      </ButtonMenu>
      <TableBorderColorSelect />
      <TableCellFillSelect />
      <Divider className={classes.buttonMenuDivider} orientation="vertical" />
      <AlineTooltip arrow title="Align table left">
        <SelectButton
          className={classes.button}
          disabled={disableTableButtons}
          selected={align === 'left'}
          onClick={() => alignTable('left')}
        >
          <LeftAlignIcon />
        </SelectButton>
      </AlineTooltip>
      <AlineTooltip arrow title="Align table center">
        <SelectButton
          className={classes.button}
          disabled={disableTableButtons}
          selected={align === 'center'}
          onClick={() => alignTable('center')}
        >
          <CenterAlignIcon />
        </SelectButton>
      </AlineTooltip>
      <AlineTooltip arrow title="Align table right">
        <SelectButton
          className={classes.button}
          disabled={disableTableButtons}
          selected={align === 'right'}
          onClick={() => alignTable('right')}
        >
          <RightAlignIcon />
        </SelectButton>
      </AlineTooltip>
      <AlineTooltip arrow title={isNestedTable ? 'Fit table to cell' : 'Fit table to page'}>
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={fitTableToContainer}
        >
          <PageSizeIcon />
        </ToolbarButton>
      </AlineTooltip>
      <Divider className={classes.buttonMenuDivider} orientation="vertical" />
      <AlineTooltip arrow title="Insert row above">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={() => insertRows('above')}
        >
          <InsertTableRowsAboveIcon />
        </ToolbarButton>
      </AlineTooltip>
      <AlineTooltip arrow title="Insert row below">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={() => insertRows('below')}
        >
          <InsertTableRowsBelowIcon />
        </ToolbarButton>
      </AlineTooltip>
      <AlineTooltip arrow title="Delete row">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={deleteRows}
        >
          <DeleteTableRowsIcon />
        </ToolbarButton>
      </AlineTooltip>
      <Divider className={classes.buttonMenuDivider} orientation="vertical" />
      <AlineTooltip arrow title="Insert column left">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={() => insertColumns('left')}
        >
          <InsertTableColumnsLeftIcon />
        </ToolbarButton>
      </AlineTooltip>
      <AlineTooltip arrow title="Insert column right">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={() => insertColumns('right')}
        >
          <InsertTableColumnsRightIcon />
        </ToolbarButton>
      </AlineTooltip>
      <AlineTooltip arrow title="Delete column">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={deleteColumns}
        >
          <DeleteTableColumnsIcon />
        </ToolbarButton>
      </AlineTooltip>
      {tableActions.map(action => (
        <TableActionButton key={action.key} action={action} />
      ))}
      <Divider className={classes.buttonMenuDivider} orientation="vertical" />
      <AlineTooltip arrow title="Delete table">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={deleteTable}
        >
          <TrashcanIcon />
        </ToolbarButton>
      </AlineTooltip>
      <AlineTooltip arrow title="Table properties">
        <ToolbarButton
          className={classes.button}
          disabled={disableTableButtons}
          onClick={() => tablePropertiesDialogOpenVar(true)}
        >
          <PropertiesIcon />
        </ToolbarButton>
      </AlineTooltip>
    </ButtonsBarWithOverflowMenu>
  );
}

function TableActionButton({ action }: { action: TableAction }): JSX.Element {
  const { classes } = useStyles();
  const closeOverflowMenu = useCloseOverflowMenu();
  const { enabled, icon: Icon, label, onClick } = action;

  return (
    <AlineTooltip arrow title={label}>
      <ToolbarButton
        className={classes.button}
        disabled={!enabled}
        onClick={() => {
          closeOverflowMenu();
          onClick();
        }}
      >
        <Icon strokeWidth={1.5} />
      </ToolbarButton>
    </AlineTooltip>
  );
}
